/* eslint-disable react/no-array-index-key */
import { Suspense } from 'react';
import dynamic from 'next/dynamic';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { Location } from 'location.d';
import type { CardPreviousView, MatrixComponent } from 'matrix.d';
import { MATRIX_TYPE } from 'constants/index';

import { CarouselWrapper } from './components/CarouselWrapper/CarouselWrapper';
import { CarouselWrapperSkeleton } from './components/CarouselWrapper/CarouselWrapper.skeleton';
import { CarouselTop10 } from 'components/CarouselTop10/CarouselTop10';
import { LastCallEvents } from 'components/LastCallEvents/LastCallEvents';

import { container, ContainerTopsort } from './Matrix.css';
import { usePathname } from 'next/navigation';

const BannerTopsort = dynamic(
  () => import('components/BannerTopsort/BannerTopsort'),
  { ssr: false }
);

interface MatrixProps {
  components: MatrixComponent[];
  userLocation: Location | null;
  cardPreviousView: CardPreviousView;
  matrixUuid: string;
  matrixType: string;
  specialUUIDs?: string[];
  prefixTrackingName?: string;
  hideTitle?: boolean;
  isCollectionSearchVariant?: boolean;
  isMobile?: boolean;
  bannerTopsort?: {
    positionIndex: number;
    id: string;
  }[];
  pageTitleForTracking?: string;
}

export function Matrix({
  components,
  userLocation,
  cardPreviousView,
  matrixUuid,
  matrixType,
  prefixTrackingName,
  specialUUIDs = [],
  hideTitle = false,
  isCollectionSearchVariant = false,
  bannerTopsort,
  pageTitleForTracking
}: MatrixProps) {
  const pathname = usePathname();

  const isHomePage = pathname === '/';

  const shouldShowLastCall = useFeatureIsOn('discovery-home-last-call');

  const lastCallConfig = useFeatureValue<{ positionIndex: number }>(
    'discovery-home-last-call',
    { positionIndex: 3 }
  );

  const targetIndex = lastCallConfig?.positionIndex ?? 3;

  return (
    <div className={container} data-testid="matrix-container">
      {components.map((component, index) => {
        const response =
          component.response?.data ||
          component.response?.matrices ||
          [];
        const banner = bannerTopsort?.find(
          (banner) => banner.positionIndex === index
        );
        const hasMatrixTop10 =
          prefixTrackingName === MATRIX_TYPE.MATRIX &&
          component.response_type === 'collection-card';

        return (
          <div key={`${component.uuid}-${index}`}>
            <Suspense
              fallback={
                <CarouselWrapperSkeleton
                  type={component.response_type}
                  numberOfComponents={response?.length || 1}
                />
              }
            >
              <CarouselWrapper
                key={`${component.uuid}-${index}`}
                component={component}
                prefixTrackingName={
                  prefixTrackingName || matrixType || MATRIX_TYPE.MATRIX
                }
                matrixUuid={matrixUuid}
                matrixType={matrixType}
                cardPreviousView={cardPreviousView}
                userLocation={userLocation}
                specialUUIDs={specialUUIDs}
                hideTitle={hideTitle}
                isCollectionSearchVariant={isCollectionSearchVariant}
                pageTitleForTracking={pageTitleForTracking}
              />
            </Suspense>

            {hasMatrixTop10 && <CarouselTop10 />}

            {isHomePage && shouldShowLastCall && index == targetIndex && (
              <LastCallEvents />
            )}

            {banner && (
              <div className={ContainerTopsort}>
                <BannerTopsort
                  idBanner={banner?.id}
                  marginBottom
                  pageTitleForTracking={pageTitleForTracking}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
